import { useMemo, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { monthLabel } from "@/components/ScopePicker";
import { Chip, ListShell, PersonRow, fmtMelb, matches, melbMonths } from "./shared";
import { useTeamReport } from "./useTeamReport";

const LIGHT = {
  green: ["bg-emerald-500", "DTM, MG1 or launch this month"],
  yellow: ["bg-amber-400", "Some activity, no DTM / MG1 / launch"],
  red: ["bg-red-500", "No activity recorded"],
};
const METRICS = [["launches", "Launches"], ["mg1s", "MG1s"], ["dtms", "DTMs"], ["pointed_to_solution", "Point to Solution"],
  ["mg0s", "MG0s"], ["contact_exchanges", "Contact Exch."], ["conversations", "Conversations"]];

function Legend() {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-600" data-testid="perf-legend">
      {Object.entries(LIGHT).map(([k, [c, t]]) => (
        <span key={k} className="inline-flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-full ${c}`} />{t}</span>
      ))}
    </div>
  );
}

function Metrics({ r }) {
  return (
    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-600 tabular-nums" data-testid={`perf-metrics-${r.id}`}>
      {METRICS.map(([k, l]) => <span key={k}><b className="text-[#0A1F44]">{r[k]}</b> {l}</span>)}
      <span className="text-slate-500" title="Informational only — not used for ranking">
        <b className="text-[#0A1F44]">{r.appointments}</b> process appts completed
      </span>
      <span className="text-slate-400 w-full">
        {r.last_occurred_at ? `Last activity ${fmtMelb(r.last_occurred_at)}` : "No activity on record"}
        {r.last_recorded_at ? ` · last recorded ${fmtMelb(r.last_recorded_at)}` : ""}
      </span>
    </div>
  );
}

export default function PerformanceView({ scope, q, rowProps }) {
  const months = useMemo(() => melbMonths(), []);
  const [month, setMonth] = useState(months[0]);
  const [filter, setFilter] = useState("all");
  const data = useTeamReport("performance", { scope, month, top: filter === "top" });
  const rows = data ? data.rows.filter((r) => matches(r, q) && (filter !== "red" || r.light === "red")) : null;
  const t = data?.top;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="h-8 w-44 text-xs" data-testid="perf-month"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-80">{months.map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
        </Select>
        <Chip active={filter === "all"} onClick={() => setFilter("all")} testid="perf-filter-all">All partners</Chip>
        <Chip active={filter === "top"} onClick={() => setFilter("top")} testid="perf-filter-top">Top 20%</Chip>
        <Chip active={filter === "red"} onClick={() => setFilter("red")} testid="perf-filter-red">No activity recorded</Chip>
      </div>
      <Legend />
      {filter === "top" && t && (
        <p className="text-[11px] text-slate-600 bg-[#C9A961]/10 rounded-md p-2" data-testid="perf-top-note">
          Top 20% of {t.denominator} partners = {t.quota} place{t.quota === 1 ? "" : "s"}; showing {t.shown} (ties at the boundary included).
          {t.qualifying < t.quota && ` Only ${t.qualifying} partner${t.qualifying === 1 ? " has" : "s have"} any ranked activity this month, so no zero-activity partners are listed.`}
        </p>
      )}
      {data?.incomplete_history_pbos > 0 && (
        <p className="text-[11px] text-amber-900 bg-amber-50 rounded-md p-2" data-testid="perf-incomplete">
          {data.incomplete_history_pbos} older appointment record(s) have no completion date and aren't counted.
        </p>
      )}
      <ListShell rows={rows} testid="perf-list" empty={q ? "No one matches your search." : filter === "red" ? "Everyone has activity recorded this month." : "No partners in this group yet."}>
        {(rows || []).map((r) => (
          <PersonRow key={r.id} u={r} prefix="perf" {...rowProps}
                     lead={<div className="w-9 shrink-0 text-center pt-0.5">
                       <div className="font-serif text-lg text-[#0A1F44] leading-none" data-testid={`perf-rank-${r.id}`}>#{r.rank}</div>
                       <span className={`inline-block mt-1.5 w-3 h-3 rounded-full ${LIGHT[r.light][0]}`} data-testid={`perf-light-${r.id}`}
                             title={LIGHT[r.light][1]} aria-label={LIGHT[r.light][1]} />
                     </div>}>
            <Metrics r={r} />
          </PersonRow>
        ))}
      </ListShell>
    </div>
  );
}
